/**
 * Public landing page served at "/" for unauthenticated visitors.
 * Authenticated users are redirected straight to the app.
 *
 * Sections: sticky header → hero → features → how-it-works → booking form
 * (public POST /public/demo-requests; requests appear in the platform
 * console) → footer.
 */
import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRightIcon,
  BellAlertIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentChartBarIcon,
  EnvelopeIcon,
  FaceSmileIcon,
  LanguageIcon,
  PhoneIcon,
  SunIcon,
} from '@heroicons/react/24/outline';
import { LocaleSwitcher } from '@/components/LocaleSwitcher';
import { submitDemoRequest } from '@/api/demoRequests';
import { useAuthStore } from '@/stores/authStore';

const CONTACT_EMAIL = 'info@ailab.am';
const CONTACT_PHONE_TEL = '+374717492';
const CONTACT_PHONE_DISPLAY = '+374 717 492';

// ---------------------------------------------------------------------------
// Booking form
// ---------------------------------------------------------------------------

function BookingForm() {
  const { t } = useTranslation(['landing']);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  // Honeypot — invisible to humans; bots that fill it are dropped server-side.
  const [website, setWebsite] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      setValidationError(t('landing:book.form.required'));
      return;
    }
    setValidationError(null);
    setState('sending');
    try {
      await submitDemoRequest({
        name: name.trim(),
        phone: phone.trim(),
        website: website || undefined,
      });
      setState('success');
    } catch {
      setState('error');
    }
  };

  if (state === 'success') {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center">
        <CheckCircleIcon className="mx-auto h-12 w-12 text-emerald-500" aria-hidden="true" />
        <p className="mt-4 text-lg font-medium text-emerald-900">
          {t('landing:book.form.success')}
        </p>
        <button
          type="button"
          onClick={() => {
            setName('');
            setPhone('');
            setState('idle');
          }}
          className="mt-4 text-sm font-medium text-emerald-700 underline underline-offset-2 hover:text-emerald-900"
        >
          {t('landing:book.form.successAnother')}
        </button>
      </div>
    );
  }

  const inputClass =
    'block w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8"
      noValidate
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="book-name" className={labelClass}>
            {t('landing:book.form.name')} *
          </label>
          <input
            id="book-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('landing:book.form.namePlaceholder')}
            autoComplete="name"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="book-phone" className={labelClass}>
            {t('landing:book.form.phone')} *
          </label>
          <input
            id="book-phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={t('landing:book.form.phonePlaceholder')}
            autoComplete="tel"
            className={inputClass}
          />
        </div>
      </div>

      {/* Honeypot field — visually hidden, excluded from tab order */}
      <div className="hidden" aria-hidden="true">
        <label htmlFor="book-website">Website</label>
        <input
          id="book-website"
          type="text"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      {validationError && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {validationError}
        </p>
      )}
      {state === 'error' && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {t('landing:book.form.error')}
        </p>
      )}

      <button
        type="submit"
        disabled={state === 'sending'}
        className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {state === 'sending' ? t('landing:book.form.sending') : t('landing:book.form.submit')}
        {state !== 'sending' && <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Decorative hero card — a miniature "today" attendance feed
// ---------------------------------------------------------------------------

function HeroMock() {
  const { t } = useTranslation(['landing']);
  const rows: { initials: string; time: string; kind: 'in' | 'late' | 'vacation' }[] = [
    { initials: 'ԱՄ', time: '08:58', kind: 'in' },
    { initials: 'ՍՀ', time: '09:02', kind: 'in' },
    { initials: 'ԳԿ', time: '09:34', kind: 'late' },
    { initials: 'ԼՊ', time: '—', kind: 'vacation' },
  ];
  const kindLabel = {
    in: t('landing:hero.mock.checkedIn'),
    late: t('landing:hero.mock.late'),
    vacation: t('landing:hero.mock.onVacation'),
  } as const;
  const kindStyle = {
    in: 'bg-emerald-50 text-emerald-700',
    late: 'bg-red-50 text-red-600',
    vacation: 'bg-sky-50 text-sky-700',
  } as const;

  return (
    <div className="relative mx-auto w-full max-w-md" aria-hidden="true">
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-900">
            {t('landing:hero.mock.title')}
          </span>
          <span className="flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-2.5 w-2.5 animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
          </span>
        </div>
        <ul className="space-y-3">
          {rows.map((row) => (
            <li key={row.initials} className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-700">
                {row.initials}
              </span>
              <span className="flex-1 text-sm tabular-nums text-gray-500">{row.time}</span>
              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${kindStyle[row.kind]}`}
              >
                {kindLabel[row.kind]}
              </span>
            </li>
          ))}
        </ul>
      </div>
      {/* Floating recognition toast */}
      <div className="absolute -bottom-5 -left-4 flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 shadow-lg sm:-left-8">
        <FaceSmileIcon className="h-5 w-5 text-indigo-600" aria-hidden="true" />
        <span className="text-xs font-medium text-gray-700">
          {t('landing:hero.mock.checkedIn')} · 08:58
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function LandingPage() {
  const { t, i18n } = useTranslation(['landing', 'common']);
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn());

  // SEO: keep the document title/description in the visitor's language.
  // The static Armenian defaults live in index.html for first paint/crawlers.
  useEffect(() => {
    document.title = t('landing:seo.title');
    document.documentElement.lang = i18n.language;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', t('landing:seo.description'));
  }, [t, i18n.language]);

  // Existing users bookmark the root URL — send them straight to the app.
  if (isLoggedIn) {
    return <Navigate to="/dashboard" replace />;
  }

  const appName = t('common:appName');

  const features = [
    { key: 'terminal', icon: FaceSmileIcon },
    { key: 'live', icon: ClockIcon },
    { key: 'reports', icon: DocumentChartBarIcon },
    { key: 'timeoff', icon: SunIcon },
    { key: 'notifications', icon: BellAlertIcon },
    { key: 'languages', icon: LanguageIcon },
  ] as const;

  const steps = ['one', 'two', 'three'] as const;

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* ── Header ─────────────────────────────────────────────── */}
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white/80 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
          <a href="#top" className="flex min-w-0 items-center gap-2.5">
            <img src="/favicon.svg" alt="" className="h-8 w-8 shrink-0" />
            <span className="truncate text-sm font-semibold leading-snug sm:text-base">
              {appName}
            </span>
          </a>

          <div className="flex shrink-0 items-center gap-2">
            <LocaleSwitcher variant="header-light" />
            <Link
              to="/login"
              className="inline-flex rounded-lg border border-gray-300 px-3.5 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              {t('landing:nav.login')}
            </Link>
          </div>
        </div>
      </header>

      <main id="top">
        {/* ── Hero ─────────────────────────────────────────────── */}
        <section className="relative overflow-hidden bg-gradient-to-b from-indigo-50 via-white to-white">
          <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">
                <FaceSmileIcon className="h-4 w-4" aria-hidden="true" />
                {t('landing:hero.badge')}
              </span>
              <h1 className="mt-5 text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
                {t('landing:hero.title')}
              </h1>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-gray-600">
                {t('landing:hero.subtitle')}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a
                  href="#book"
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-6 py-3 text-base font-semibold text-white shadow-sm transition-colors hover:bg-indigo-500"
                >
                  {t('landing:hero.cta')}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
                </a>
                <Link
                  to="/login"
                  className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-6 py-3 text-base font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  {t('landing:hero.ctaSecondary')}
                </Link>
              </div>
            </div>
            <HeroMock />
          </div>
        </section>

        {/* ── Features ─────────────────────────────────────────── */}
        <section id="features" className="scroll-mt-16 py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight">{t('landing:features.title')}</h2>
              <p className="mt-3 text-gray-600">{t('landing:features.subtitle')}</p>
            </div>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {features.map(({ key, icon: Icon }) => (
                <div
                  key={key}
                  className="rounded-2xl border border-gray-200 p-6 transition-shadow hover:shadow-md"
                >
                  <span className="inline-flex rounded-xl bg-indigo-50 p-2.5">
                    <Icon className="h-6 w-6 text-indigo-600" aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 text-base font-semibold">
                    {t(`landing:features.items.${key}.title`)}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600">
                    {t(`landing:features.items.${key}.text`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── How it works ─────────────────────────────────────── */}
        <section id="how" className="scroll-mt-16 bg-gray-50 py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <h2 className="text-center text-3xl font-bold tracking-tight">
              {t('landing:how.title')}
            </h2>
            <div className="mt-12 grid gap-10 md:grid-cols-3">
              {steps.map((step, i) => (
                <div key={step} className="relative text-center md:text-left">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-indigo-600 text-lg font-bold text-white">
                    {i + 1}
                  </span>
                  <h3 className="mt-4 text-lg font-semibold">
                    {t(`landing:how.steps.${step}.title`)}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600">
                    {t(`landing:how.steps.${step}.text`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Booking ──────────────────────────────────────────── */}
        <section id="book" className="scroll-mt-16 py-20">
          <div className="mx-auto grid max-w-6xl gap-12 px-4 sm:px-6 lg:grid-cols-2">
            <div>
              <h2 className="text-3xl font-bold tracking-tight">{t('landing:book.title')}</h2>
              <p className="mt-3 max-w-md text-gray-600">{t('landing:book.subtitle')}</p>

              <div className="mt-10">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                  {t('landing:book.contactTitle')}
                </h3>
                <div className="mt-4 space-y-3">
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="flex items-center gap-3 text-gray-700 transition-colors hover:text-indigo-600"
                  >
                    <span className="inline-flex rounded-lg bg-indigo-50 p-2">
                      <EnvelopeIcon className="h-5 w-5 text-indigo-600" aria-hidden="true" />
                    </span>
                    <span>
                      <span className="block text-xs text-gray-400">
                        {t('landing:book.emailLabel')}
                      </span>
                      <span className="text-sm font-medium">{CONTACT_EMAIL}</span>
                    </span>
                  </a>
                  <a
                    href={`tel:${CONTACT_PHONE_TEL}`}
                    className="flex items-center gap-3 text-gray-700 transition-colors hover:text-indigo-600"
                  >
                    <span className="inline-flex rounded-lg bg-indigo-50 p-2">
                      <PhoneIcon className="h-5 w-5 text-indigo-600" aria-hidden="true" />
                    </span>
                    <span>
                      <span className="block text-xs text-gray-400">
                        {t('landing:book.phoneLabel')}
                      </span>
                      <span className="text-sm font-medium tabular-nums">
                        {CONTACT_PHONE_DISPLAY}
                      </span>
                    </span>
                  </a>
                </div>
              </div>
            </div>
            <BookingForm />
          </div>
        </section>
      </main>

      {/* ── Footer ───────────────────────────────────────────── */}
      <footer className="border-t border-gray-200 py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-4 text-sm text-gray-500 sm:flex-row sm:px-6">
          <div className="flex items-center gap-2">
            <img src="/favicon.svg" alt="" className="h-6 w-6" />
            <span>{appName}</span>
          </div>
          <p>
            © {new Date().getFullYear()} · {t('landing:footer.rights')}
          </p>
          <div className="flex items-center gap-4">
            <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-gray-700">
              {CONTACT_EMAIL}
            </a>
            <a href={`tel:${CONTACT_PHONE_TEL}`} className="tabular-nums hover:text-gray-700">
              {CONTACT_PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
